// 화면 18 — TanStack Query 훅 (폴링 300s, ADR-DASH-06).
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '../../api/client';
import {
  getBandHistory,
  getLeaderStocksDaily,
  getLeaderStocksDiff,
  getStockOhlcv,
} from './api';
import type {
  BandHistory,
  LeaderStocksDiff,
  LeaderStocksSnapshot,
  LookbackDays,
  OhlcvInterval,
  StockOhlcv,
} from './types';

const POLL_MS = 300_000; // 300s (PRD §5.8.4 — 일 1회 적재)

// date 가 null 이면 서버가 max(trading_date) 폴백 — 화면 첫 진입 / 휴장일 모두 안전.
export function useLeaderStocksDaily(date: string | null) {
  return useQuery<LeaderStocksSnapshot, ApiError>({
    queryKey: ['leading-stocks', 'daily', date ?? 'latest'],
    queryFn: () => getLeaderStocksDaily(date),
    refetchInterval: POLL_MS,
    staleTime: POLL_MS - 1000,
    retry: (failureCount, err) => {
      const status = (err as ApiError | undefined)?.httpStatus;
      // 400 / 404 는 재시도 무의미.
      if (status && [400, 404].includes(status)) return false;
      return failureCount < 1;
    },
  });
}

// v1.10.3: prevDays 는 정수 (1 | 5 | 30) 만. 422 invalid_range 케이스 폐기.
// date 는 daily 응답의 trading_date 를 그대로 받음 → 폴백 일자와 자동 동기화.
export function useLeaderStocksDiff(date: string | null, prevDays: LookbackDays) {
  return useQuery<LeaderStocksDiff, ApiError>({
    queryKey: ['leading-stocks', 'diff', date, prevDays],
    queryFn: () => getLeaderStocksDiff(date as string, prevDays),
    refetchInterval: POLL_MS,
    staleTime: POLL_MS - 1000,
    enabled: Boolean(date),
    retry: (failureCount, err) => {
      const status = (err as ApiError | undefined)?.httpStatus;
      if (status && [400, 404].includes(status)) return false;
      return failureCount < 1;
    },
  });
}

export function useBandHistory(symbol: string | null, days = 60) {
  return useQuery<BandHistory, ApiError>({
    queryKey: ['leading-stocks', 'band-history', symbol, days],
    queryFn: () => getBandHistory(symbol as string, days),
    refetchInterval: POLL_MS,
    staleTime: POLL_MS - 1000,
    enabled: Boolean(symbol),
    retry: (failureCount, err) => {
      const status = (err as ApiError | undefined)?.httpStatus;
      if (status && [400, 404, 422].includes(status)) return false;
      return failureCount < 1;
    },
  });
}

// 캔들 차트용 OHLCV — drawer 열림 + symbol 있을 때만 enabled.
export function useStockOhlcv(
  symbol: string | null,
  interval: OhlcvInterval,
  days: number,
  enabled: boolean
) {
  return useQuery<StockOhlcv, ApiError>({
    queryKey: ['stocks', 'ohlcv', symbol, interval, days],
    queryFn: () => getStockOhlcv(symbol as string, interval, days),
    refetchInterval: POLL_MS,
    staleTime: POLL_MS - 1000,
    enabled: enabled && Boolean(symbol),
    retry: (failureCount, err) => {
      const status = (err as ApiError | undefined)?.httpStatus;
      if (status && [400, 404, 422].includes(status)) return false;
      return failureCount < 1;
    },
  });
}
